/**
 * GeolocationBanner — 1-click geocoding banner for projects missing GPS coords.
 *
 * Shown at the top of ProjectDetailPage when `!project.coords`. Unlocks all
 * downstream features that need GPS (Blueprint Aligner cadastre + Static Maps
 * satellite + future Sprint 32 Google Maps compositing).
 *
 * States :
 *  - project has `address` → single button "Géocoder cette adresse"
 *  - project has no address → inline input with LIVE ADDRESS AUTOCOMPLETE
 *    (debounced /api/geocode/suggest — Nominatim OSM + own project matches)
 *  - dismissible per project via sessionStorage
 *
 * Long-term vision: the address-autocomplete brick powered by
 * `useAddressSuggest` is reusable in the future HUB (saved addresses list,
 * perso/pro categories, right-panel project options).
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, X, Loader2, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAddressSuggest } from '../../hooks/useAddressSuggest';
import SaveToHubDialog from '../hub/SaveToHubDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GeolocationBanner({ project, onGeocoded }) {
  const dismissKey = useMemo(
    () => (project?.project_id ? `geo-banner-dismissed-${project.project_id}` : null),
    [project?.project_id],
  );
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined' || !dismissKey) return false;
    return window.sessionStorage.getItem(dismissKey) === '1';
  });
  const [address, setAddress] = useState(project?.address || '');
  const [busy, setBusy] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [saveToHub, setSaveToHub] = useState(null); // { coords, address } after geocode
  const wrapperRef = useRef(null);

  const { items: suggestions, loading: suggesting } = useAddressSuggest(address, {
    minChars: 3,
    delayMs: 250,
    limit: 6,
  });

  // Close the dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (!project || project.coords || dismissed) {
    // Even after geocoding, keep rendering the SaveToHubDialog while it's open
    if (saveToHub) {
      return (
        <SaveToHubDialog
          open={!!saveToHub}
          onOpenChange={(o) => { if (!o) setSaveToHub(null); }}
          address={saveToHub.address}
          coords={saveToHub.coords}
          projectId={project?.project_id}
          defaultLabel={project?.name || ''}
        />
      );
    }
    return null;
  }

  const dismiss = () => {
    if (dismissKey) window.sessionStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  // Applies a project patch: coords + address, then notifies parent
  const applyGeocode = async ({ coords, addressText }) => {
    const patch = { coords };
    if (!project.address && addressText) patch.address = addressText;
    const r = await axios.put(
      `${API}/projects/${project.project_id}`,
      patch,
      { withCredentials: true },
    );
    toast.success('Projet géolocalisé — cadastre & satellite débloqués');
    onGeocoded?.(r.data);
    // Trigger the "Save to HUB" prompt right after — dialog is rendered from the
    // post-geocode branch of this component so it survives the banner unmount.
    setSaveToHub({ coords, address: addressText || project.address || '' });
  };

  // Manual geocode via typed text (POST /geocode)
  const geocode = async () => {
    const q = (address || '').trim();
    if (!q) {
      toast.error('Renseigne une adresse');
      return;
    }
    setBusy(true);
    setShowSuggest(false);
    try {
      const g = await axios.post(
        `${API}/geocode`,
        { address: q },
        { withCredentials: true },
      );
      if (!g.data?.found || !g.data?.coords) {
        toast.error('Adresse introuvable sur OpenStreetMap');
        return;
      }
      await applyGeocode({ coords: g.data.coords, addressText: g.data.display_name || q });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de géocodage');
    } finally {
      setBusy(false);
    }
  };

  // Pick a suggestion — reuse its lat/lon directly, no extra Nominatim hit
  const pickSuggestion = async (s) => {
    if (!s || s.lat == null || s.lon == null) {
      // Project match without coords: fall back to text geocode
      setAddress(s?.sub || s?.label || address);
      setShowSuggest(false);
      return;
    }
    setBusy(true);
    setShowSuggest(false);
    try {
      const label = s.sub || s.label || '';
      setAddress(label);
      await applyGeocode({ coords: [s.lat, s.lon], addressText: label });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur d\'enregistrement');
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowSuggest(true);
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && suggestions[highlight]) {
        e.preventDefault();
        pickSuggestion(suggestions[highlight]);
      } else {
        geocode();
      }
    } else if (e.key === 'Escape') {
      setShowSuggest(false);
    }
  };

  const hasStoredAddress = !!(project.address || '').trim();

  return (
    <div
      data-testid="geolocation-banner"
      className="mx-4 sm:mx-6 lg:mx-8 mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1 flex items-center gap-2 text-[12px] relative z-30"
      style={{ backdropFilter: 'blur(6px)' }}
      title="Débloque le cadastre, la tuile satellite et les futurs modules 3D en géocodant l'adresse."
    >
      <MapPin className="w-3.5 h-3.5 shrink-0 text-amber-400" />
      <span className="font-medium text-amber-100 whitespace-nowrap">
        Pas de coordonnées GPS
      </span>
      <span className="hidden lg:inline text-amber-200/60 truncate">
        — géocode l&apos;adresse pour débloquer cadastre &amp; satellite
      </span>
      <span className="flex-1" />

      {hasStoredAddress ? (
        <>
          <span
            className="hidden md:inline-block text-[12px] text-amber-100/90 truncate max-w-[280px]"
            title={project.address}
          >
            {project.address}
          </span>
          <Button
            size="sm"
            onClick={geocode}
            disabled={busy}
            className="bg-amber-500 hover:bg-amber-400 text-black h-6 px-2 text-[11px]"
            data-testid="geolocation-banner-geocode-existing"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MapPin className="w-3 h-3 mr-1" />}
            Géocoder
          </Button>
        </>
      ) : (
        <div ref={wrapperRef} className="relative">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-amber-300/70 pointer-events-none" />
            <Input
              value={address}
              onChange={(e) => { setAddress(e.target.value); setShowSuggest(true); setHighlight(-1); }}
              onFocus={() => setShowSuggest(true)}
              onKeyDown={onKeyDown}
              placeholder="Tape 3 lettres… ex: 12 rue de la Paix"
              className="h-6 w-64 pl-7 text-[11px] bg-black/30 border-amber-500/40 text-amber-50 placeholder:text-amber-200/50"
              data-testid="geolocation-banner-address-input"
              autoComplete="off"
            />
          </div>

          {showSuggest && (address || '').trim().length >= 3 && (
            <div
              className="absolute z-50 top-full mt-1 left-0 w-96 rounded-md border border-amber-500/40 bg-neutral-900/95 shadow-xl overflow-hidden"
              style={{ backdropFilter: 'blur(8px)' }}
              data-testid="geolocation-banner-suggestions"
            >
              {suggesting && (
                <div className="px-3 py-2 text-[12px] text-amber-200/70 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Recherche…
                </div>
              )}
              {!suggesting && suggestions.length === 0 && (
                <div className="px-3 py-2 text-[12px] text-amber-200/60">Aucune suggestion</div>
              )}
              {!suggesting && suggestions.map((s, i) => (
                <button
                  type="button"
                  key={`${s.kind}-${s.project_id || i}-${s.lat || 'na'}`}
                  onClick={() => pickSuggestion(s)}
                  onMouseEnter={() => setHighlight(i)}
                  className={`w-full text-left px-3 py-2 border-b border-amber-500/10 last:border-b-0 flex items-start gap-2 text-[12px] ${highlight === i ? 'bg-amber-500/20' : 'hover:bg-amber-500/10'}`}
                  data-testid={`geolocation-banner-suggest-item-${i}`}
                >
                  <MapPin className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${s.kind === 'project' ? 'text-emerald-400' : 'text-amber-300'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-amber-50 truncate">{s.label}</div>
                    {s.sub && <div className="text-amber-200/60 truncate">{s.sub}</div>}
                  </div>
                  {s.kind === 'project' && (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-400/80 shrink-0 mt-0.5">projet</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!hasStoredAddress && (
        <Button
          size="sm"
          onClick={geocode}
          disabled={busy || !address.trim()}
          className="bg-amber-500 hover:bg-amber-400 text-black h-6 px-2 text-[11px]"
          data-testid="geolocation-banner-geocode-new"
        >
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MapPin className="w-3 h-3 mr-1" />}
          Géocoder
        </Button>
      )}

      <button
        type="button"
        onClick={dismiss}
        className="p-1 rounded hover:bg-amber-500/20 text-amber-200/70 hover:text-amber-100 shrink-0"
        title="Masquer pour cette session"
        data-testid="geolocation-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
